import { useState, useContext, useEffect, useLayoutEffect, useMemo, useRef, useCallback, memo } from 'react';
import { useTranslation } from 'react-i18next';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import Autocomplete from '@mui/material/Autocomplete';
import Tooltip from '@mui/material/Tooltip';
import Chip from '@mui/material/Chip';
import MenuItem from '@mui/material/MenuItem';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import StraightenIcon from '@mui/icons-material/Straighten';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import BookmarkAddOutlinedIcon from '@mui/icons-material/BookmarkAddOutlined';

import { stoneSort } from '../docFormat';
import AuthContext from '../../authAndConnections/auth';
import AxiosGlobal from '../../authAndConnections/axiosGlobalUrl';
import SpecCodeBuilder from '../../inventory/specCodeBuilder';
import { latinDigits } from './palletTemplates';
import { advanceFrom } from '../../../tools/enterAdvancesFields';
import { parseStoneCode } from '../../inventory/util/codeParser';
import useFlipList, { prefersReducedMotion } from '../../../tools/hooks/useFlipList';

// The pallet rows of a form, and everything that happens to them: one definition, used by the packing list form and the label form, so the
// area rule, the Enter and Insert keys, the sort menu and the motion cannot differ between the two.

// `thicknessAuto`: the thickness was filled in from the list's standard thickness, not typed. Client-only (the save maps
// explicit fields) - it lets the standard thickness be TYPED digit by digit ("1.8" passes through "1") without the first
// digit being left behind in every row.
export const EMPTY_ITEM = { code: '', sort: '', lengthCm: '', widthCm: '', thicknessCm: '', thicknessAuto: false, pcs: '', sqm: '', sqmTouched: false };
export const hasValue = (v) => v !== '' && v !== undefined && v !== null;

// The sort of a cut of stone, chosen on every row. Mirrors STONE_SORTS in api/utils/packingListFields.js (the server
// checks it again: anything else is stored as "not given").
const STONE_SORTS = ['Q', 'E', 'W'];

// ── The item grid ───────────────────────────────────────────────────────────
// ONE column definition, used by the header and by every row, so they cannot
// drift apart. It was two independent flex lines of fixed pixel widths before,
// which is how the numbers became unreadable: a flex child shrinks by default,
// so six 62px fields plus a 150px code in a drawer narrower than their sum were
// squeezed to slivers — on a phone down to a sliver that fits a caret and
// nothing else. `minmax(Npx, …)` cannot go below N; the code column takes the
// slack instead.
// The first column is the row's number inside its pallet (1, 2, 3 ...), then the cells that are typed in.
// The m² cell got its own, wider track: four-decimal text ("19.5000") plus its recalc icon no longer fit the
// 76px a 1fr share used to leave it — found from a real screenshot where the value ran in under the trash icon.
const ITEM_GRID = '26px minmax(90px, 1.5fr) minmax(58px, 0.65fr) repeat(4, minmax(52px, 0.7fr)) minmax(112px, 1.3fr) 30px';

// A number input's spinner arrows eat ~17px INSIDE an already tight field and
// are useless for a measurement nobody nudges one at a time. inputMode still
// gives a phone the numeric keypad.
const NO_SPINNERS = {
  '& input[type=number]': { MozAppearance: 'textfield' },
  '& input::-webkit-outer-spin-button, & input::-webkit-inner-spin-button': {
    WebkitAppearance: 'none', margin: 0,
  },
};
export const EMPTY_PALLET = { palletId: '', reference: '', productCode: '', processingType: '', items: [{ ...EMPTY_ITEM }] };

// Every pallet row carries a `_key` that is its identity while the form is open (it is never sent to the server). The
// rows can be re-ordered, and a React key made of the row's POSITION would then hand one row's field state (the open
// processing-type box, the focused input) to whichever row slid into its place.
let palletKeySeq = 0;
export const newPalletKey = () => { palletKeySeq += 1; return `pk${Date.now().toString(36)}-${palletKeySeq}`; };
export const withKey = (p) => (p && p._key ? p : { ...p, _key: newPalletKey() });

// Common processing descriptions seen on the real packing lists. freeSolo —
// the typed text IS the stored value, so these are only suggestions.
const PROCESSING_SUGGESTIONS = ['FLD (tile)', 'UNFLD (slab)', 'CRSCT (tile)', 'VNCT (tile)', 'Blocks', 'Cut-to-size'];

// An area is kept to FOUR decimals: 17.2800, not 17.28. A stored value with at most four decimals loads padded to
// four (same number, so nothing changes in the data); one with more - typed by hand - is left exactly as it is.
export const fixed4 = (value) => {
  const n = Number(value);
  if (value === '' || value == null || !Number.isFinite(n)) return value == null ? '' : value;
  return Math.abs(n * 10000 - Math.round(n * 10000)) < 1e-6 ? n.toFixed(4) : value;
};

// Every pallet of ONE list needs its own ID and its own reference (the server refuses a list otherwise).
// Case and extra spaces do not make two values different ("a 1" = "A  1"), an empty reference is "not given"
// rather than a duplicate of another empty one, and only rows that will really be saved take part: a row
// with no ID is dropped on save.
export const palletNorm = (v) => String(v == null ? '' : v).trim().replace(/\s+/g, ' ').toLowerCase();

// A pallet code is unique across EVERY list (api/utils/palletCodes.js, whose codeKey this mirrors: keep them in step). The keyboard's
// digits and leading zeros do not make a new code (03000 = 3000 = ۰۳۰۰۰). One left empty takes the next number of the series at Save.
export const palletCodeKey = (v) => {
  const text = palletNorm(latinDigits(v));
  return /^\d+$/.test(text) ? text.replace(/^0+(?=\d)/, '') : text;
};
export const formatPalletCode = (n) => String(n).padStart(5, '0');

// -> { id: { [row]: [IDs of the other pallets it clashes with] }, ref: { [row]: [...] },
//      groups: [{ field: 'palletId' | 'reference', value, pallets: [IDs] }] }
export function palletClashes(pallets) {
  const out = { id: {}, ref: {}, groups: [] };
  for (const [field, bucket] of [['palletId', 'id'], ['reference', 'ref']]) {
    const byValue = new Map();
    pallets.forEach((p, row) => {
      if (!palletNorm(p.palletId)) return;
      const key = field === 'reference' ? palletCodeKey(p[field]) : palletNorm(p[field]);
      if (!key) return;
      if (!byValue.has(key)) byValue.set(key, []);
      byValue.get(key).push(row);
    });
    for (const rows of byValue.values()) {
      if (rows.length < 2) continue;
      const ids = rows.map((r) => String(pallets[r].palletId).trim());
      out.groups.push({ field, value: String(pallets[rows[0]][field]).trim(), pallets: ids });
      rows.forEach((r) => { out[bucket][r] = ids.filter((_, i) => rows[i] !== r); });
    }
  }
  return out;
}

// A default ID no pallet of the list already has (removing P2 of three and adding one must not hand out P3 twice).
function freePalletId(pallets) {
  const used = new Set(pallets.map((p) => palletNorm(p.palletId)));
  let n = pallets.length + 1;
  while (used.has(palletNorm(`P${n}`))) n += 1;
  return `P${n}`;
}
// ... and for a copy: P1-copy, then P1-copy2 ...
function copyPalletId(pallets, id) {
  const base = `${String(id || '').trim() || 'P'}-copy`;
  const used = new Set(pallets.map((p) => palletNorm(p.palletId)));
  if (!used.has(palletNorm(base))) return base;
  let n = 2;
  while (used.has(palletNorm(`${base}${n}`))) n += 1;
  return `${base}${n}`;
}

// Mirrors the server's productPrefix() in routes/mis/packingLists.js — a
// packing item's code is a NOMINAL product code (TR09), so scope is validated
// as a product-code prefix match, not a foreign key. Kept in sync so the form
// can warn before the save round-trips into a 400.
export const productPrefix = (code) => {
  if (!code) return '';
  const m = String(code).toUpperCase().match(/^([A-Z]{2}\d{2})/);
  return m ? m[1] : String(code).toUpperCase();
};

// m² for one item row. Unsized slabs (L or W = 0) can't be derived, so those
// keep whatever was typed.
const deriveSqm = (it) => {
  const l = Number(it.lengthCm) || 0, w = Number(it.widthCm) || 0, p = Number(it.pcs) || 0;
  if (!l || !w || !p) return null;
  // (L / 100) x (W / 100) x pieces, rounded to four decimals and kept as TEXT so the field reads 17.2800. It is worked
  // out as l*w*p/10000 in whole ten-thousandths (the 1e-9 keeps a true .5 from falling the wrong way on float noise).
  return (Math.round(l * w * p + 1e-9) / 10000).toFixed(4);
};

// A row the person has put ANYTHING into: a stone code, a sort, a size, a count. The thickness that came from the "standard
// thickness" box (thicknessAuto) is the form's, not theirs. Saving keeps every such row - a stone code is NOT required (a pallet of
// one stone is often typed as sizes and counts alone, and throwing those rows away at Save was a silent loss of the whole list).
export const rowHasData = (it) => ['code', 'sort', 'lengthCm', 'widthCm', 'pcs', 'sqm'].some((k) => String(it[k] == null ? '' : it[k]).trim() !== '')
  || (String(it.thicknessCm == null ? '' : it.thicknessCm).trim() !== '' && !it.thicknessAuto);

// A row nobody has filled in yet: no sizes / counts, and no code beyond the pallet's own.
const isBlankRow = (it, pallet) => !String(it.pcs || '') && !String(it.sqm || '')
  && !String(it.lengthCm || '') && !String(it.widthCm || '')
  && (!String(it.code || '').trim() || String(it.code).trim() === String(pallet.productCode || '').trim());

// The "No" box of a pallet row: its place in the list, shown as the row's number. Typing another number and leaving the
// box (or pressing Enter) moves the pallet there; 0, nothing or a non-number moves nothing; Escape drops the typed number
// (and does not close the form, as it would from any other field). It keeps the number being typed to ITSELF: held in the
// form, every keystroke would re-draw every pallet row - on a list of 30 that is the difference between typing and waiting.
const NO_FIELD = { size: 'small', InputLabelProps: { shrink: true } };
function PalletNoInput({ place, palletKey, index, label, help, onMove }) {
  const [text, setTextState] = useState(null);   // the number being typed; null = not editing, show the place
  const textRef = useRef(null);                  // ...also in a ref, so a blur that follows Enter cannot commit twice
  const setText = (v) => { textRef.current = v; setTextState(v); };
  const commit = () => {
    const typed = textRef.current;
    setText(null);
    if (typed == null) return;
    const to = parseInt(typed, 10);
    if (Number.isFinite(to) && to >= 1) onMove(palletKey, to);
  };
  return (
    <Tooltip title={help}>
      <TextField {...NO_FIELD} label={label} value={text == null ? String(place) : text}
        onFocus={(e) => { setText(String(place)); e.target.select(); }}
        onChange={(e) => setText(e.target.value.replace(/\D/g, '').slice(0, 4))}
        onBlur={commit}
        inputProps={{
          inputMode: 'numeric', 'aria-label': label, 'data-testid': `pl-pallet-no-${index}`, 'data-pallet-no': palletKey,
          // Enter commits and leaves the box - and, claiming the key, keeps the app-wide "Enter moves to the next field"
          // from also firing into a row that has just moved.
          enterKeyHint: 'done',
          onKeyDown: (e) => {
            if (e.key === 'Enter') { e.preventDefault(); commit(); e.currentTarget.blur(); }
            else if (e.key === 'Escape' && textRef.current != null) { e.stopPropagation(); setText(null); e.currentTarget.blur(); }
          },
        }}
        sx={{ width: { xs: 68, sm: 62 }, '& input': { textAlign: 'center', fontWeight: 700, fontVariantNumeric: 'tabular-nums' } }} />
    </Tooltip>
  );
}

// Defined at MODULE scope on purpose. A component declared inside the form body
// is a NEW component type on every render, so React unmounts and remounts its
// whole subtree on each keystroke — which drops focus and sends the caret out of
// the input you are typing in (and resets tab position). Keep section/field
// helpers out here and pass what they need as props.
export function Section({ label, hint, children, T }) {
  return (
    <Box sx={{ mb: 2.5 }}>
      <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1, mb: 1.25 }}>
        <Typography sx={{ fontSize: '0.66rem', fontWeight: 700, textTransform: 'uppercase',
          letterSpacing: 1, color: T.TEXT_TER }}>{label}</Typography>
        {hint && <Typography sx={{ fontSize: '0.66rem', color: T.TEXT_TER }}>{hint}</Typography>}
        <Box sx={{ flex: 1, height: '1px', bgcolor: T.BD }} />
      </Box>
      {children}
    </Box>
  );
}

// ── Pallet rows ─────────────────────────────────────────────────────────────
// Two memoized components, so that typing in ONE pallet does not draw the other thirty-two again. The form keeps every pallet
// in one array and used to re-render the whole form on each keystroke - unnoticeable on a desktop, seconds per key on a phone
// with a 33-pallet list (measured on the production build under a 6x CPU slow-down: 3.6 s a keystroke, 4 s a move). A `pallet`
// object keeps its identity until IT changes (the mutators copy only the row they edit) and every handler is stable and takes
// the row's `_key`, so React.memo's shallow compare skips every row that did not change. The head (No, arrows, ID, reference,
// processing) and the item area (sizes, counts, m², the specification finder) are separate memos: editing a reference does not
// redraw the item rows. Keep the handlers stable (useCallback, refs and updater functions only) or the memo stops paying.

// Phone sizing, for this form's own drawer only. iOS Safari zooms the whole page when a field with text under 16px takes
// focus, and the compact type that suits a pointer is wrong for a thumb; 44px is the smallest comfortable touch target (the job
// report form follows the same rule). `!important` because several fields set their own size (monospace identifiers, the item
// code) and the zoom happens whatever the reason for the small text.
const PHONE_TARGET = 44;
export const PHONE_BTN = { width: PHONE_TARGET, height: PHONE_TARGET };
export const PHONE_FORM_SX = {
  '& .MuiOutlinedInput-root.MuiInputBase-sizeSmall': { minHeight: PHONE_TARGET },
  '& input:not([type=checkbox]):not([type=radio]), & textarea, & select': { fontSize: '16px !important' },
  '& .MuiToggleButton-root': { minHeight: PHONE_TARGET },
};

export const FIELD = { size: 'small', InputLabelProps: { shrink: true } };
// A figure in the item grid: right-aligned tabular numbers without the spinner arrows stealing width. Geometry comes from the grid.
const NUM_CELL_SX = {
  ...NO_SPINNERS,
  minWidth: 0,
  '& input': { textAlign: 'right', fontVariantNumeric: 'tabular-nums', px: 1 },
};
// the value selects on focus, so a wrong entry can just be overtyped
const SELECT_ON_FOCUS = { onFocus: (e) => e.target.select() };

// The sizes, counts and m² of one pallet, with its "add row" / "find by specification" row.
const PalletItems = memo(function PalletItems({
  palletKey, items, productCode, isXs, T, type, scopePrefixes, codeOptions, specOpen, canCreateVariety, searchProducts,
  onAddItem, onEnterRow, onRemoveItem, onItemField, onResetSqm, onToggleSpec, onUseBuilt,
}) {
  const { t } = useTranslation();
  // Enter on the LAST cell (m²) of a row inserts the next row RIGHT AFTER it, with the cursor in its stone code - so a
  // pallet is filled, or a cut added in the middle of one, without leaving the keyboard. A row nothing has been typed
  // into is not extended: Enter there moves on as it does everywhere in the app, which is also the way out of the pallet.
  const onLastCellEnter = (e, iIdx, it) => {
    if (e.key !== 'Enter' || e.isComposing || e.shiftKey || e.ctrlKey || e.metaKey || e.altKey) return;
    if (isBlankRow(it, { productCode })) return;
    e.preventDefault();   // the document-level Enter handler stands down for a key somebody already claimed
    onEnterRow(palletKey, iIdx);
  };
  // The sort of a cut. A real MUI Select, not a native one: Enter has to OPEN it (the owner's words), and only a menu of
  // our own can be opened by a key - a native <select> cannot, in any way a browser agrees on. It is marked
  // [data-enter-opens] so the app-wide Enter handler lets the key through (see tools/enterAdvancesFields.js), and after a
  // pick made with the keyboard the focus moves ON to the next cell: without that, Enter would re-open the menu for ever.
  const sortPicked = (e) => {
    if (!e || e.key !== 'Enter') return;      // a mouse pick leaves the focus where it is, as the place lists do
    // after the tick MUI has put the focus back on the select itself, which is what we advance FROM
    setTimeout(() => advanceFrom(document.activeElement), 0);
  };
  const pt = items.reduce((acc, it) => {
    acc.pcs += Number(it.pcs) || 0;
    acc.sqm += Number(it.sqm) || 0;
    return acc;
  }, { pcs: 0, sqm: 0 });
  // On a phone the column headers are gone, so every field carries its own label; on a desktop the header row says it once
  // and a label here would be noise.
  const labelled = (text) => (isXs ? { label: text, InputLabelProps: { shrink: true } } : {});
  const footerButton = { fontSize: '0.66rem', textTransform: 'none', ...(isXs ? { minHeight: PHONE_TARGET, fontSize: '0.8rem', px: 1.5 } : {}) };

  return (
    <>
      {/* Column headers belong to the desktop table only - on a phone each row is a card and carries its own labels. */}
      {!isXs && (
        <Box sx={{ display: 'grid', gridTemplateColumns: ITEM_GRID, gap: 0.75,
          px: 1.5, pt: 1.25, pb: 0.5, alignItems: 'center' }}>
          {[
            { label: t('mis.plNoField'), align: 'center' },
            { label: t('mis.plColCode'), align: 'start' },
            { label: t('mis.plColSort'), align: 'start' },
            { label: t('mis.plColLength') },
            { label: t('mis.plColWidth') },
            { label: t('mis.plColThickness') },
            { label: t('mis.plColPcs') },
            { label: t('mis.plColSqm') },
          ].map((c) => (
            <Typography key={c.label} noWrap sx={{ minWidth: 0,
              fontSize: '0.6rem', fontWeight: 700, letterSpacing: 0.4,
              textTransform: 'uppercase', color: T.TEXT_TER,
              textAlign: c.align === 'start' ? 'start' : c.align === 'center' ? 'center' : 'end' }}>
              {c.label}
            </Typography>
          ))}
          <Box />
        </Box>
      )}

      {/* Item rows */}
      <Box sx={{ px: 1.5, pb: 1 }}>
        {items.map((it, iIdx) => {
          const derived = deriveSqm(it);
          const isAuto = !it.sqmTouched && derived !== null;
          const badCode = type === 'linked' && it.code && scopePrefixes.size > 0
            && !scopePrefixes.has(productPrefix(it.code));
          return (
            <Box key={iIdx} data-item-row={iIdx} sx={isXs
              ? { mb: 1.25, p: 1.25, borderRadius: '12px',
                  border: `1px solid ${T.BD}`, display: 'grid',
                  gridTemplateColumns: '1fr 1fr', gap: 1.25, alignItems: 'end',
                  bgcolor: iIdx % 2 ? T.ROW_ALT : 'transparent' }
              // every second row is shaded so the eye can stay on one row across the cells; the row is widened by its own
              // padding (mx -0.5 / px 0.5) so the cells still line up with the column headings above
              : { display: 'grid', gridTemplateColumns: ITEM_GRID, gap: 0.75,
                  mx: -0.5, px: 0.5, py: 0.5, mb: 0.25, borderRadius: '8px', alignItems: 'center',
                  bgcolor: iIdx % 2 ? T.ROW_ALT : 'transparent' }}>
              {/* the row's number in this pallet: it follows the order, so inserting or removing a row renumbers the ones after it.
                  Not an input, so it is never a stop for Tab / Enter / the arrows. */}
              <Typography data-item-no={iIdx + 1} sx={{ fontVariantNumeric: 'tabular-nums', fontWeight: 700, color: T.TEXT_TER,
                ...(isXs ? { gridColumn: '1 / -1', fontSize: '0.72rem', letterSpacing: 0.4, textTransform: 'uppercase' }
                  : { fontSize: '0.7rem', textAlign: 'center' }) }}>
                {isXs ? `${t('mis.plNoField')} ${iIdx + 1}` : iIdx + 1}
              </Typography>
              <Box sx={{ minWidth: 0, gridColumn: isXs ? '1 / -1' : 'auto' }}>
                {type === 'linked' ? (
                  <Autocomplete freeSolo options={codeOptions} fullWidth
                    value={it.code || ''}
                    onInputChange={(_, v) => onItemField(palletKey, iIdx, 'code', v)}
                    renderInput={(params) => (
                      <TextField {...params} size="small" error={Boolean(badCode)}
                        {...labelled(t('mis.plColCode'))}
                        inputProps={{ ...params.inputProps, 'data-item-code': '' }}
                        placeholder={t('mis.itemCodePlaceholder')}
                        sx={{ '& input': { fontFamily: 'monospace', fontSize: isXs ? '0.95rem' : '0.75rem' } }} />
                    )} />
                ) : (
                  <TextField size="small" value={it.code} fullWidth
                    {...labelled(t('mis.plColCode'))}
                    inputProps={{ 'data-item-code': '' }}
                    placeholder={t('mis.itemCodePlaceholder')}
                    onChange={(e) => onItemField(palletKey, iIdx, 'code', e.target.value)}
                    sx={{ '& input': { fontFamily: 'monospace', fontSize: isXs ? '0.95rem' : '0.75rem' } }} />
                )}
              </Box>
              <TextField select size="small" value={it.sort || ''} sx={{ minWidth: 0 }}
                {...labelled(t('mis.plColSort'))}
                SelectProps={{
                  displayEmpty: true,
                  onClose: sortPicked,
                  SelectDisplayProps: { 'data-item-sort': '', 'data-enter-opens': 'true', 'aria-label': t('mis.plColSort') },
                  // a menu belongs above whatever surface opened it (see the portal z-index note in CLAUDE.md)
                  MenuProps: { sx: { zIndex: (theme) => theme.zIndex.modal + 2 }, MenuListProps: { dense: !isXs } },
                }}
                onChange={(e) => onItemField(palletKey, iIdx, 'sort', e.target.value)}>
                <MenuItem value="">{t('mis.plSortNone')}</MenuItem>
                {STONE_SORTS.map((s) => <MenuItem key={s} value={s}>{s}</MenuItem>)}
              </TextField>
              <TextField size="small" type="number" value={it.lengthCm} sx={NUM_CELL_SX}
                {...labelled(t('mis.plColLength'))}
                {...SELECT_ON_FOCUS} inputProps={{ inputMode: 'decimal', min: 0 }}
                onChange={(e) => onItemField(palletKey, iIdx, 'lengthCm', e.target.value)} />
              <TextField size="small" type="number" value={it.widthCm} sx={NUM_CELL_SX}
                {...labelled(t('mis.plColWidth'))}
                {...SELECT_ON_FOCUS} inputProps={{ inputMode: 'decimal', min: 0 }}
                onChange={(e) => onItemField(palletKey, iIdx, 'widthCm', e.target.value)} />
              <TextField size="small" type="number" value={it.thicknessCm} sx={NUM_CELL_SX}
                {...labelled(t('mis.plColThickness'))}
                {...SELECT_ON_FOCUS} inputProps={{ inputMode: 'decimal', step: '0.1', min: 0 }}
                onChange={(e) => onItemField(palletKey, iIdx, 'thicknessCm', e.target.value)} />
              <TextField size="small" type="number" value={it.pcs} sx={NUM_CELL_SX}
                {...labelled(t('mis.plColPcs'))}
                {...SELECT_ON_FOCUS} inputProps={{ inputMode: 'numeric', step: '1', min: 0 }}
                onChange={(e) => onItemField(palletKey, iIdx, 'pcs', e.target.value)} />
              <Tooltip title={isAuto ? t('mis.plSqmAuto') : t('mis.plSqmManual')}>
                <TextField size="small" type="number" value={it.sqm} sx={{
                  ...NUM_CELL_SX,
                  // four decimals ("19.5000") plus the recalc icon need a touch less padding and a slightly
                  // smaller digit size than the other number cells to sit comfortably even at the min width
                  '& input': { ...NUM_CELL_SX['& input'], pr: 0.5, fontSize: isXs ? undefined : '0.72rem' },
                  '& .MuiOutlinedInput-root': isAuto ? { bgcolor: T.CTRL_BG } : {},
                }}
                  {...labelled(t('mis.plColSqm'))}
                  {...SELECT_ON_FOCUS} inputProps={{ inputMode: 'decimal', min: 0 }}
                  onKeyDown={(e) => onLastCellEnter(e, iIdx, it)}
                  onChange={(e) => onItemField(palletKey, iIdx, 'sqm', e.target.value)}
                  InputProps={{ endAdornment: isAuto
                    ? <AutoAwesomeIcon sx={{ fontSize: 11, color: T.TEXT_TER, ml: 0.25 }} />
                    : (derived !== null && (
                        <Tooltip title={t('mis.plSqmRecalc')}>
                          {/* a finger needs more than the 16px a pointer gets */}
                          <IconButton size="small" aria-label={t('mis.plSqmRecalc')}
                            sx={isXs ? { width: 40, height: 40, p: 0, mr: -1 } : { p: 0.15 }}
                            onClick={() => onResetSqm(palletKey, iIdx)}>
                            <AutoAwesomeIcon sx={{ fontSize: isXs ? 18 : 12 }} />
                          </IconButton>
                        </Tooltip>
                      )) }} />
              </Tooltip>
              <Box sx={{ display: 'flex', justifyContent: isXs ? 'flex-end' : 'center',
                minWidth: 0, gridColumn: isXs ? '1 / -1' : 'auto' }}>
                {items.length > 1 && (
                  <IconButton size={isXs ? 'medium' : 'small'} onClick={() => onRemoveItem(palletKey, iIdx)}
                    aria-label={t('common.delete')} sx={isXs ? PHONE_BTN : undefined}>
                    <DeleteOutlineIcon sx={{ fontSize: isXs ? 18 : 14, color: T.TEXT_TER }} />
                  </IconButton>
                )}
              </Box>
            </Box>
          );
        })}

        {/* Pallet footer - add row + subtotal */}
        <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: isXs ? 0.5 : 0, mt: 0.5 }}>
          <Button size="small" startIcon={<AddIcon sx={{ fontSize: 13 }} />}
            onClick={() => onAddItem(palletKey)}
            sx={{ ...footerButton, color: T.TEXT_SEC }}>
            {t('mis.addItemRowButton')}
          </Button>
          <Button size="small" startIcon={<StraightenIcon sx={{ fontSize: 13 }} />}
            onClick={() => onToggleSpec(palletKey)}
            aria-expanded={specOpen}
            sx={{ ...footerButton, color: specOpen ? T.TEXT_PRI : T.TEXT_SEC }}>
            {specOpen ? t('inventory.specHide') : t('inventory.specToggle')}
          </Button>
          <Box sx={{ marginInlineStart: 'auto', display: 'flex', gap: 1.5, paddingInlineEnd: isXs ? 0 : 3.5 }}>
            <Typography sx={{ fontSize: '0.68rem', color: T.TEXT_SEC }}>
              {t('mis.plColPcs')} <b style={{ color: T.TEXT_PRI }}>{pt.pcs}</b>
            </Typography>
            <Typography sx={{ fontSize: '0.68rem', color: T.TEXT_SEC }}>
              {t('mis.plColSqm')} <b style={{ color: T.TEXT_PRI }}>{pt.sqm.toFixed(4)}</b>
            </Typography>
          </Box>
        </Box>
        {specOpen && (
          <Box sx={{ mt: 1 }}>
            <SpecCodeBuilder searchProducts={searchProducts} showInventory={false}
              canCreate={canCreateVariety} noPermissionHint={t('inventory.specCannotAdd')}
              usedCodes={new Set(items.map((it) => String(it.code || '').toUpperCase()))}
              onUse={(product, variant) => onUseBuilt(palletKey, product, variant)} />
          </Box>
        )}
      </Box>
    </>
  );
});

// One pallet of the list: its head (place, arrows, ID, reference, processing, copy, remove) and its item area.
export const PalletCard = memo(function PalletCard({
  pallet, index, isLast, canRemove, isMoved, clashIdText, clashRefText, takenText, codeHint, idHint, canTemplate, isXs, T, type, scopePrefixes, codeOptions,
  specOpen, canCreateVariety, searchProducts,
  onMoveBy, onMoveTo, onDuplicate, onRemove, onField, onSaveTemplate,
  onAddItem, onEnterRow, onRemoveItem, onItemField, onResetSqm, onToggleSpec, onUseBuilt,
}) {
  const { t } = useTranslation();
  const key = pallet._key;
  const arrowSx = isXs ? PHONE_BTN : { p: 0.1 };
  const iconBtnSx = isXs ? PHONE_BTN : undefined;

  // On a phone the head is three lines - [No, arrows, copy, remove] / [ID, reference] / [processing] - because one line of
  // seven controls leaves each too small to hit. The copy / remove buttons are placed before the fields on a phone and
  // after them on a desktop, so the tab order follows what is drawn either way.
  const rowButtons = (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, ...(isXs ? { marginInlineStart: 'auto', gap: 0.5 } : {}) }}>
      {canTemplate && (
        <Tooltip title={t('mis.plTemplateSave')}>
          <IconButton size="small" onClick={() => onSaveTemplate(key)} aria-label={t('mis.plTemplateSave')} data-testid={`pl-pallet-template-${index}`} sx={iconBtnSx}>
            <BookmarkAddOutlinedIcon sx={{ fontSize: isXs ? 19 : 16, color: T.TEXT_TER }} />
          </IconButton>
        </Tooltip>
      )}
      <Tooltip title={t('mis.plDuplicatePallet')}>
        <IconButton size="small" onClick={() => onDuplicate(key)} aria-label={t('mis.plDuplicatePallet')} sx={iconBtnSx}>
          <ContentCopyIcon sx={{ fontSize: isXs ? 18 : 15, color: T.TEXT_TER }} />
        </IconButton>
      </Tooltip>
      {canRemove && (
        <Tooltip title={t('mis.plRemovePallet')}>
          <IconButton size="small" onClick={() => onRemove(key)} aria-label={t('mis.plRemovePallet')} sx={iconBtnSx}>
            <DeleteOutlineIcon sx={{ fontSize: isXs ? 20 : 16, color: T.TEXT_TER }} />
          </IconButton>
        </Tooltip>
      )}
    </Box>
  );

  return (
    <Box data-pallet-key={key}
      sx={{ border: `1px solid ${isMoved ? T.TEXT_SEC : T.BD}`, borderRadius: '12px', overflow: 'hidden',
        transition: 'border-color 0.4s',
        // the row that was just moved / added pulses once, so it can be found again
        '@keyframes plMovedPulse': {
          '0%': { boxShadow: `0 0 0 0 ${T.PULSE}` },
          '30%': { boxShadow: `0 0 0 5px ${T.PULSE}` },
          '100%': { boxShadow: '0 0 0 0 transparent' },
        },
        animation: isMoved ? 'plMovedPulse 1.2s ease-out' : 'none',
        '@media (prefers-reduced-motion: reduce)': { animation: 'none', transition: 'none' } }}>

      {/* Pallet head */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 1.5, py: 1, flexWrap: 'wrap',
        bgcolor: T.CTRL_BG, borderBottom: `1px solid ${T.BD}` }}>
        {/* Its place in the list - the "No" the printed list shows. Type another number to move it there. */}
        <PalletNoInput place={index + 1} palletKey={key} index={index}
          label={t('mis.plNoField')} help={t('mis.plNoHelp')} onMove={onMoveTo} />
        <Box sx={{ display: 'flex', flexDirection: isXs ? 'row' : 'column', mx: isXs ? 0 : -0.25 }}>
          <Tooltip title={t('mis.plMoveUp')}>
            <span>
              <IconButton size="small" disabled={index === 0} onClick={() => onMoveBy(key, -1)}
                aria-label={t('mis.plMoveUp')} data-testid={`pl-pallet-up-${index}`} sx={arrowSx}>
                <KeyboardArrowUpIcon sx={{ fontSize: isXs ? 24 : 18 }} />
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip title={t('mis.plMoveDown')}>
            <span>
              <IconButton size="small" disabled={isLast} onClick={() => onMoveBy(key, 1)}
                aria-label={t('mis.plMoveDown')} data-testid={`pl-pallet-down-${index}`} sx={arrowSx}>
                <KeyboardArrowDownIcon sx={{ fontSize: isXs ? 24 : 18 }} />
              </IconButton>
            </span>
          </Tooltip>
        </Box>
        {isXs && rowButtons}
        {/* a line break: the fields start a new line on a phone, whatever the width */}
        {isXs && <Box sx={{ flexBasis: '100%', height: 0 }} />}
        <TextField {...FIELD} label={t('mis.palletIdField')} value={pallet.palletId} placeholder={idHint}
          inputProps={{ 'data-pallet-field': 'palletId' }}
          onChange={(e) => onField(key, 'palletId', e.target.value)}
          error={Boolean(clashIdText)} data-pallet-clash={clashIdText ? 'true' : undefined}
          sx={isXs ? { flex: '1 1 0', minWidth: 0 } : { width: 96 }} />
        {/* Left empty it takes the next number at Save; the placeholder shows which one it would be right now. */}
        <TextField {...FIELD} label={t('mis.referenceField')} value={pallet.reference} placeholder={codeHint}
          inputProps={{ 'data-pallet-field': 'reference', dir: 'ltr' }}
          onChange={(e) => onField(key, 'reference', e.target.value)}
          error={Boolean(clashRefText || takenText)} data-pallet-clash={clashRefText || takenText ? 'true' : undefined}
          sx={isXs ? { flex: '1 1 0', minWidth: 0 } : { width: 110 }} />
        <Autocomplete freeSolo options={PROCESSING_SUGGESTIONS}
          value={pallet.processingType || ''}
          onInputChange={(_, v) => onField(key, 'processingType', v)}
          sx={isXs ? { flex: '1 1 100%', minWidth: 0 } : { flex: 1, minWidth: 130 }}
          renderInput={(params) => (
            <TextField {...params} {...FIELD} label={t('mis.processingTypeField')} />
          )} />
        {!isXs && rowButtons}
      </Box>

      {(clashIdText || clashRefText || takenText) && (
        <Box role="alert" sx={{ px: 1.5, py: 0.5, borderBottom: `1px solid ${T.BD}` }}>
          {takenText && <Typography data-pallet-taken sx={{ fontSize: '0.68rem', color: 'error.main' }}>{takenText}</Typography>}
          {clashIdText && (
            <Typography sx={{ fontSize: '0.68rem', color: 'error.main' }}>
              {t('mis.plDuplicateIdField', { pallets: clashIdText })}
            </Typography>
          )}
          {clashRefText && (
            <Typography sx={{ fontSize: '0.68rem', color: 'error.main' }}>
              {t('mis.plDuplicateRefField', { pallets: clashRefText })}
            </Typography>
          )}
        </Box>
      )}

      {pallet.existing && (
        <Box data-pallet-existing sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1, px: 1.5, py: 0.5, borderBottom: `1px solid ${T.BD}` }}>
          <Chip size="small" variant="outlined" sx={{ height: 20, fontSize: '0.64rem' }}
            label={pallet.existing.list ? t('mis.lblOnListChip', { list: pallet.existing.list.docNumber }) : t('mis.lblExistingChip')} />
          {pallet.existing.palletizedBy && (
            <Typography noWrap sx={{ fontSize: '0.68rem', color: T.TEXT_SEC, minWidth: 0 }}>
              {t('mis.lblPalletizedByLine', { name: pallet.existing.palletizedBy })}
            </Typography>
          )}
        </Box>
      )}

      <PalletItems palletKey={key} items={pallet.items} productCode={pallet.productCode} isXs={isXs} T={T} type={type}
        scopePrefixes={scopePrefixes} codeOptions={codeOptions} specOpen={specOpen}
        canCreateVariety={canCreateVariety} searchProducts={searchProducts}
        onAddItem={onAddItem} onEnterRow={onEnterRow} onRemoveItem={onRemoveItem} onItemField={onItemField}
        onResetSqm={onResetSqm} onToggleSpec={onToggleSpec} onUseBuilt={onUseBuilt} />
    </Box>
  );
});

// What a form sends for a pallet: the fields, and only the rows with something in them.
export const palletForSave = (p) => ({
  palletId: p.palletId, reference: p.reference,
  productCode: p.productCode, processingType: p.processingType,
  items: p.items.filter(rowHasData).map((it) => ({
    code: it.code,
    sort: it.sort || '',
    lengthCm:    it.lengthCm    === '' ? undefined : Number(it.lengthCm),
    widthCm:     it.widthCm     === '' ? undefined : Number(it.widthCm),
    thicknessCm: it.thicknessCm === '' ? undefined : Number(it.thicknessCm),
    pcs:         it.pcs         === '' ? undefined : Number(it.pcs),
    sqm:         it.sqm         === '' ? undefined : Number(it.sqm),
  })),
});

// A pallet record as a row of a form. `existing` is client-only: what the card says about it, and how the row looked when it came in.
export const palletFromRecord = (rec) => {
  const row = {
    ...EMPTY_PALLET,
    palletRecordId: rec._id, palletId: rec.palletId || '', reference: rec.reference || '',
    productCode: rec.productCode || '', processingType: rec.processingType || '',
    items: rec.items && rec.items.length
      ? rec.items.map((it) => ({
          ...EMPTY_ITEM, code: it.code || '', sort: stoneSort(it.sort),
          lengthCm: it.lengthCm ?? '', widthCm: it.widthCm ?? '', thicknessCm: it.thicknessCm ?? '', pcs: it.pcs ?? '',
          sqm: fixed4(it.sqm), sqmTouched: true,      // as saved, until L, W or pcs changes
        }))
      : [{ ...EMPTY_ITEM }],
  };
  return { ...row, existing: {
    list: rec.list || null, palletizedBy: rec.palletizedBy || '', destinationAddress: rec.destinationAddress || '',
    sig: JSON.stringify(palletForSave(row)),
  } };
};

// A pallet nobody has put anything into: the empty row a new form opens with, which an added pallet takes the place of.
const isUntouchedPallet = (p) => !p.palletRecordId && !p.items.some(rowHasData) && !String(p.reference || '').trim() && !String(p.processingType || '').trim();

// A pallet has something worth saving: a row, a code or a processing type.
export const palletHasContent = (p) => p.items.some(rowHasData) || Boolean(String(p.reference || '').trim()) || Boolean(String(p.processingType || '').trim());

// The rows of a form and every change to them. `autoIds`: new pallets are numbered P1, P2 ... (a packing list); off, they start with no ID (a
// label, which has no load to be numbered on). `thickness`: the list's standard thickness, which new rows start from.
export function usePalletEditor({ thickness = '', branchId, autoIds = true }) {
  const { t } = useTranslation();
  const authCtx = useContext(AuthContext);
  const axiosGlobal = useContext(AxiosGlobal);
  const [pallets, setPallets] = useState(() => [withKey({ ...EMPTY_PALLET, palletId: 'P1' })]);
  const [specKey, setSpecKey] = useState(null);   // _key of the pallet whose specification finder is open
  // Re-ordering: the row that just moved (briefly highlighted, and scrolled into view, so it can be found again).
  const [movedKey, setMovedKey] = useState(null);
  const [announce, setAnnounce] = useState('');          // the move said aloud, for a screen reader
  const exitingRef = useRef(new Set());                  // rows that are folding away (a second click must not remove another)
  // Motion (tools/hooks/useFlipList.js): a pallet that is moved, added, duplicated or removed does not jump - the rows slide
  // to their new places, a new row eases in, a removed one folds away. Only a request to move is animated (capture() runs
  // just before such a change), never typing; and `prefers-reduced-motion` turns all of it off.
  const orderSignature = pallets.map((p) => p._key).join('|');
  const flip = useFlipList(orderSignature, { selector: '[data-pallet-key]', attr: 'palletKey', liftKey: movedKey });
  // A pallet whose place changed - because it was moved, or because a row above it came or went - rolls its number: the
  // new figure slides in from the way the pallet travelled (up when the number got smaller), so the eye follows the change.
  const placesRef = useRef(new Map());
  useLayoutEffect(() => {
    const root = flip.containerRef.current;
    const before = placesRef.current;
    const now = new Map(pallets.map((p, i) => [p._key, i + 1]));
    placesRef.current = now;
    if (!root || !before.size || prefersReducedMotion()) return;
    now.forEach((place, key) => {
      const was = before.get(key);
      if (was === undefined || was === place) return;
      const box = root.querySelector(`input[data-pallet-no="${key}"]`);
      if (!box || typeof box.animate !== 'function') return;
      box.getAnimations().forEach((a) => { if (a.id === 'flip-no') a.cancel(); });   // a quick second move starts from rest
      const roll = box.animate(
        [{ transform: `translateY(${place < was ? 9 : -9}px)`, opacity: 0.1 }, { transform: 'none', opacity: 1 }],
        { duration: 320, easing: 'cubic-bezier(0.22, 0.9, 0.3, 1)' },
      );
      try { roll.id = 'flip-no'; } catch (_) { /* a read-only id changes nothing */ }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderSignature]);

  // ── mutators ──────────────────────────────────────────────────────────────
  // Every pallet handler takes the pallet's `_key` (never its position) and is STABLE: it reaches the latest values through
  // refs and updater functions only. That is what lets the memoized rows (PalletCard / PalletItems, above) skip a render when
  // some OTHER pallet changed - the cost of a keystroke on a phone used to be "draw every pallet again".
  const palletsRef = useRef(pallets);
  palletsRef.current = pallets;
  const thicknessRef = useRef(thickness);
  thicknessRef.current = thickness;
  const autoIdsRef = useRef(autoIds);
  autoIdsRef.current = autoIds;
  const nextId = (list) => (autoIdsRef.current ? freePalletId(list) : '');
  const { capture: flipCapture, exit: flipExit } = flip;

  // After a keyboard-driven add the focus goes where the person types next: the new pallet's ID, or the new row's stone code.
  const pendingFocusRef = useRef(null);
  useEffect(() => {
    const want = pendingFocusRef.current;
    if (!want) return;
    pendingFocusRef.current = null;
    const card = document.querySelector(`[data-pallet-key="${want.key}"]`);
    if (!card) return;
    let target = null;
    if (want.what === 'pallet') {
      target = card.querySelector('input[data-pallet-field="palletId"]');
    } else {
      const rows = card.querySelectorAll('[data-item-row]');
      const row = rows[want.row === undefined ? rows.length - 1 : Math.min(want.row, rows.length - 1)];
      target = row && row.querySelector('input[data-item-code]');
    }
    if (target) { target.focus(); if (typeof target.select === 'function') target.select(); }
  }, [pallets]);

  // A new row eases in where it lands and is brought into view (it is "the moved one" for the highlight and the scroll).
  // `focusIt` (the Insert key) also puts the cursor in the new pallet's ID.
  // Pallets made from a template, as many as were asked for: its rows, each pallet with its own ID and its own code (left empty, so each
  // takes the next one at Save, and they come out consecutive). An untouched pallet at the end of the list - the empty P1 of a new form -
  // is the first of them rather than left behind.
  const addFromTemplate = (template, count = 1) => {
    const keys = Array.from({ length: count }, newPalletKey);    // outside the updater: React may run an updater twice
    const rows = () => (template.items || []).map((it) => ({
      ...EMPTY_ITEM,
      code: it.code || '', sort: stoneSort(it.sort),
      lengthCm: it.lengthCm ?? '', widthCm: it.widthCm ?? '', thicknessCm: it.thicknessCm ?? '', pcs: it.pcs ?? '',
      sqm: fixed4(it.sqm), sqmTouched: true,      // as saved, until L, W or pcs changes
    }));
    const fields = () => {
      const items = rows();
      return { productCode: template.productCode || '', processingType: template.processingType || '', reference: '', items: items.length ? items : [{ ...EMPTY_ITEM }] };
    };
    flipCapture();
    setPallets((ps) => {
      const next = ps.slice();
      keys.forEach((key, i) => {
        const last = next[next.length - 1];
        const untouched = i === 0 && last && !last.items.some(rowHasData) && !String(last.reference || '').trim() && !String(last.processingType || '').trim();
        if (untouched) next[next.length - 1] = { ...last, ...fields(), _key: key };
        else next.push({ ...EMPTY_PALLET, ...fields(), _key: key, palletId: nextId(next) });
      });
      return next;
    });
    setMovedKey(keys[0]);    // the first of them is brought into view, so the person sees where they start
    setAnnounce(t('mis.plTemplateAdded', { name: template.name, count }));
  };

  const addPallet = (focusIt = false) => {
    const key = newPalletKey();
    if (focusIt) pendingFocusRef.current = { key, what: 'pallet' };
    flipCapture();
    setPallets((ps) => [...ps, {
      ...EMPTY_PALLET,
      _key: key,
      palletId: nextId(ps),
      items: [blankItem()],
    }]);
    setMovedKey(key);
  };

  const duplicatePallet = useCallback((key) => {
    const copyKey = newPalletKey();
    flipCapture();
    setSpecKey(null);
    setPallets((ps) => {
      const idx = ps.findIndex((p) => p._key === key);
      if (idx < 0) return ps;
      const src = ps[idx];
      // a copy is a new pallet: it takes nothing of the original's identity
      const { palletRecordId, existing, ...rest } = src;
      const copy = {
        ...rest,
        _key: copyKey,
        palletId: autoIdsRef.current ? copyPalletId(ps, src.palletId) : '',
        reference: '',
        items: src.items.map((it) => ({ ...it })),
      };
      return [...ps.slice(0, idx + 1), copy, ...ps.slice(idx + 1)];
    });
    setMovedKey(copyKey);
  }, [flipCapture]);

  // The row folds away first, and only then leaves the list - so the rows below close up with it instead of jumping. The
  // last pallet stays (counting the rows that are already folding away, so two quick taps cannot empty the list).
  const removePallet = useCallback((key) => {
    if (!key || exitingRef.current.has(key)) return;
    if (palletsRef.current.length - exitingRef.current.size <= 1) return;
    exitingRef.current.add(key);
    setSpecKey(null);
    flipExit(key, () => {
      setPallets((ps) => ps.filter((p) => p._key !== key));
      exitingRef.current.delete(key);
    });
  }, [flipExit]);

  // Re-ordering. The pallets print in the order they are listed here, and the "No" is simply the place in that order.
  // A move that changes nothing (the same place, or the end of the list) does nothing at all - it must not close the
  // specification finder or light up the row. The row is found by its key INSIDE the updater as well, so the move is
  // right even if the list changed since this render.
  const reorder = useCallback((key, targetOf) => {
    const list = palletsRef.current;
    const here = list.findIndex((p) => p._key === key);
    const there = here < 0 ? -1 : Math.max(0, Math.min(list.length - 1, targetOf(here)));
    if (here < 0 || there === here) return;
    flipCapture();   // where every row is NOW, so each one can slide from there to where it is next
    setPallets((ps) => {
      const from = ps.findIndex((p) => p._key === key);
      if (from < 0) return ps;
      const target = Math.max(0, Math.min(ps.length - 1, targetOf(from)));
      if (target === from) return ps;
      const next = ps.slice();
      const [row] = next.splice(from, 1);
      next.splice(target, 0, row);
      return next;
    });
    setSpecKey(null);   // a move closes the specification finder: it would be in the way of the row sliding past
    setMovedKey(key);
    setAnnounce(t('mis.plMovedTo', { pallet: String(list[here].palletId || '').trim() || here + 1, place: there + 1 }));
  }, [flipCapture, t]);
  const movePalletBy = useCallback((key, delta) => reorder(key, (from) => from + delta), [reorder]);
  // The place typed in a row's "No" box (1-based; past the end means last).
  const movePalletTo = useCallback((key, place) => reorder(key, () => place - 1), [reorder]);
  useEffect(() => {
    if (!movedKey) return undefined;
    const row = document.querySelector(`[data-pallet-key="${movedKey}"]`);
    if (row && row.scrollIntoView) row.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
    const timer = setTimeout(() => setMovedKey(null), 1600);
    return () => clearTimeout(timer);
  }, [movedKey]);

  const setPalletField = useCallback((key, field, value) =>
    setPallets((ps) => ps.map((p) => (p._key === key ? { ...p, [field]: value } : p))), []);

  const blankItem = () => ({ ...EMPTY_ITEM, thicknessCm: thicknessRef.current, thicknessAuto: hasValue(thicknessRef.current) });
  const addItem = useCallback((key) => setPallets((ps) => ps.map((p) => (
    p._key === key
      ? { ...p, items: [...p.items, { ...blankItem(), code: p.productCode || '' }] }
      : p
  ))), []);

  // A row inserted right after row `iIdx` (what Enter on that row's last cell does), with the cursor landing in it.
  const insertItemAfter = useCallback((key, iIdx) => setPallets((ps) => ps.map((p) => {
    if (p._key !== key) return p;
    const at = Math.min(Math.max(iIdx + 1, 0), p.items.length);
    return { ...p, items: [...p.items.slice(0, at), { ...blankItem(), code: p.productCode || '' }, ...p.items.slice(at)] };
  })), []);

  // Enter on the last cell of a row: the row after it, with the cursor in its stone code.
  const enterRow = useCallback((key, iIdx) => {
    pendingFocusRef.current = { key, what: 'item', row: iIdx + 1 };
    insertItemAfter(key, iIdx);
  }, [insertItemAfter]);

  // Products + their varieties for the specification builder: this branch's own catalogue.
  const searchForBuilder = useCallback(async (term) => {
    const res = await authCtx.jwtInst({
      method: 'get', url: `${axiosGlobal.defaultTargetApi}/mis/products-lookup`,
      params: { search: String(term || '').trim(), branchId },
    });
    return res.data || [];
  }, [authCtx, axiosGlobal, branchId]);

  // A variety from the specification builder becomes an item of that pallet: it fills the
  // pallet's empty row (or adds one) with the variety's code and its own size. An unsized slab
  // brings only its thickness — the real cut is typed, as for any other row.
  const addBuiltItem = useCallback((key, product, variant) => {
    const parsed = parseStoneCode(variant.code);
    const sized = parsed && parsed.valid && !parsed.unsized;
    setPallets((ps) => ps.map((p) => {
      if (p._key !== key) return p;
      const blankAt = p.items.findIndex((it) => isBlankRow(it, p));
      const base = blankAt >= 0 ? p.items[blankAt] : { ...EMPTY_ITEM, thicknessCm: thicknessRef.current };
      const item = {
        ...base,
        code: variant.code,
        ...(sized ? { lengthCm: String(parsed.lengthCm), widthCm: String(parsed.widthCm) } : {}),
        ...(parsed && parsed.valid ? { thicknessCm: String(parsed.thicknessMm / 10), thicknessAuto: false } : {}),
        sqmTouched: false,
      };
      const derived = deriveSqm(item);               // the sizes just changed: the area follows (it needs the count too)
      if (derived !== null) item.sqm = derived;
      const items = blankAt >= 0 ? p.items.map((it, j) => (j === blankAt ? item : it)) : [...p.items, item];
      return { ...p, productCode: p.productCode || product.code, items };
    }));
  }, []);

  const removeItem = useCallback((key, iIdx) => setPallets((ps) => ps.map((p) => (
    p._key === key ? { ...p, items: p.items.filter((_, j) => j !== iIdx) } : p
  ))), []);

  // The area is calculated again on EVERY change of L, W or pcs - a hand-typed area and one loaded from a saved list included:
  // the figure on the row always follows its sizes (asked for 2026-10-27). A hand-typed area lasts until the next such change.
  // When it cannot be calculated (L or W is 0 - an unsized slab sold by area - or the count is empty), a hand-typed area stays,
  // but one the form calculated is cleared rather than left behind, stale.
  const setItemField = useCallback((key, iIdx, field, value) => setPallets((ps) => ps.map((p) => {
    if (p._key !== key) return p;
    return {
      ...p,
      items: p.items.map((it, j) => {
        if (j !== iIdx) return it;
        const next = { ...it, [field]: value };
        if (field === 'thicknessCm') next.thicknessAuto = false;
        if (field === 'sqm') { next.sqmTouched = value !== ''; return next; }
        if (['lengthCm', 'widthCm', 'pcs'].includes(field)) {
          const derived = deriveSqm(next);
          if (derived !== null) { next.sqm = derived; next.sqmTouched = false; }
          else if (!it.sqmTouched) next.sqm = '';
        }
        return next;
      }),
    };
  })), []);

  const resetSqm = useCallback((key, iIdx) => setPallets((ps) => ps.map((p) => {
    if (p._key !== key) return p;
    return { ...p, items: p.items.map((it, j) => {
      if (j !== iIdx) return it;
      const derived = deriveSqm(it);
      return { ...it, sqmTouched: false, sqm: derived === null ? '' : derived };
    }) };
  })), []);

  const toggleSpec = useCallback((key) => setSpecKey((cur) => (cur === key ? null : key)), []);

  // Pallets that already exist, added by their code. They go after what is on the form; an empty first row gives up its place. On a packing
  // list each keeps its P number when it has one nothing else on the list uses, and gets the next free one otherwise.
  const addExisting = useCallback((records) => {
    if (!records.length) return;
    const keys = records.map(() => newPalletKey());    // outside the updater: React may run an updater twice
    flipCapture();
    setPallets((ps) => {
      const next = ps.slice();
      records.forEach((rec, i) => {
        const base = palletFromRecord(rec);
        const last = next[next.length - 1];
        const blank = i === 0 && last && isUntouchedPallet(last) ? next.pop() : null;
        const free = (id) => Boolean(palletNorm(id)) && !next.some((p) => palletNorm(p.palletId) === palletNorm(id));
        let palletId = base.palletId;
        if (autoIdsRef.current && !free(palletId)) palletId = blank && free(blank.palletId) ? blank.palletId : freePalletId(next);
        next.push({ ...base, _key: keys[i], palletId });
      });
      return next;
    });
    setMovedKey(keys[0]);
    setAnnounce(t('mis.lblAddedAnnounce', { count: records.length }));
  }, [flipCapture, t]);

  // Insert adds a pallet wherever in the form the cursor is (Shift+Insert is paste and Ctrl+Insert copy: left alone).
  const onInsertKey = (event, saving) => {
    if (event.key !== 'Insert' || event.repeat || event.shiftKey || event.ctrlKey || event.metaKey || event.altKey) return;
    event.preventDefault();
    if (!saving) addPallet(true);
  };

  return {
    pallets, setPallets, palletsRef, specKey, setSpecKey, movedKey, announce, flip,
    addPallet, addFromTemplate, addExisting, onInsertKey, duplicatePallet, removePallet, movePalletBy, movePalletTo, setPalletField,
    addItem, enterRow, removeItem, setItemField, resetSqm, toggleSpec, addBuiltItem, searchForBuilder,
  };
}

// The pallet codes of a form: the next free one (the placeholder of a code left empty, looked at and not taken), and the codes on the form that
// a list or another pallet already holds. `ownPallets`: what the list being edited already carries, whose codes are not asked about.
export function usePalletCodes({ open, pallets, palletsRef, ownPallets, excludeListId, refreshOn }) {
  const { t } = useTranslation();
  const authCtx = useContext(AuthContext);
  const axiosGlobal = useContext(AxiosGlobal);
  const [nextCode, setNextCode] = useState(null);
  const [takenCodes, setTakenCodes] = useState({});

  // What a code left empty would get: the next numbers of the series, past any typed on this form. Only a suggestion (the server
  // takes the real ones at Save), which is why it is a placeholder and never a value.
  const codeHints = useMemo(() => {
    const hints = {};
    if (nextCode == null) return hints;
    const typed = new Set(pallets.map((p) => palletCodeKey(p.reference)).filter(Boolean));
    let n = nextCode;
    pallets.forEach((p) => {
      if (palletCodeKey(p.reference)) return;
      while (typed.has(String(n))) n += 1;
      hints[p._key] = formatPalletCode(n);
      n += 1;
    });
    return hints;
  }, [pallets, nextCode]);

  // The codes to ask the server about: the ones that are new to this list. A code the list already had is not checked again, so a
  // list saved before codes were unique can still be edited (the server follows the same rule).
  const ownCodes = useMemo(() => new Set((ownPallets || []).map((p) => palletCodeKey(p.reference)).filter(Boolean)), [ownPallets]);
  const newCodes = useMemo(() => [...new Set(pallets.map((p) => latinDigits(p.reference).trim()).filter((c) => c && !ownCodes.has(palletCodeKey(c))))], [pallets, ownCodes]);
  const newCodesSignature = newCodes.join('\n');
  const takenFor = (reference) => takenCodes[palletCodeKey(reference)] || null;
  const takenLine = (row) => (row.docNumber == null
    ? t('mis.lblCodeTakenField')
    : t('mis.plCodeTakenField', { list: row.branchName ? `#${row.docNumber} (${row.branchName})` : `#${row.docNumber}` }));
  const takenError = (row) => (row.docNumber == null
    ? t('mis.lblCodeTakenError', { code: row.code })
    : t('mis.plCodeTakenError', { code: row.code, list: `#${row.docNumber}` }));

  // The next pallet code, for the placeholders. Looked at, not taken.
  useEffect(() => {
    if (!open) return undefined;
    let cancelled = false;
    authCtx.jwtInst({ method: 'get', url: `${axiosGlobal.defaultTargetApi}/mis/packing-lists/next-pallet-code` })
      .then((res) => { if (!cancelled) setNextCode(Number(res.data && res.data.next) || null); })
      .catch(() => { if (!cancelled) setNextCode(null); });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, refreshOn]);

  // A pallet code that another list already carries is flagged as it is typed. The save checks again: this answer can be a moment
  // out of date. (A query string by hand: axios would send an array as code[]=.)
  useEffect(() => {
    if (!open) return undefined;
    if (!newCodes.length) { setTakenCodes({}); return undefined; }
    let cancelled = false;
    const timer = setTimeout(() => {
      const qs = new URLSearchParams();
      newCodes.forEach((code) => qs.append('code', code));
      if (excludeListId) qs.set('excludeId', excludeListId);
      palletsRef.current.forEach((p) => { if (p.palletRecordId) qs.append('recordId', p.palletRecordId); });
      authCtx.jwtInst({ method: 'get', url: `${axiosGlobal.defaultTargetApi}/mis/packing-lists/pallet-codes-check?${qs}` })
        .then((res) => { if (!cancelled) setTakenCodes(Object.fromEntries(((res.data && res.data.taken) || []).map((row) => [row.key, row]))); })
        .catch(() => { /* the save will say it */ });
    }, 400);
    return () => { cancelled = true; clearTimeout(timer); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, newCodesSignature]);

  return { codeHints, takenFor, takenLine, takenError, setTakenCodes };
}
